"use client";

import { MenuItem, MenuSection } from "react-aria-components";
import { Text } from "@zapier/design-system";
import { Icon } from "@zapier/zinnia-icons";
import styles from "./SearchDropdown.module.css";

interface SearchHistoryProps {
  history: string[];
  onSelect: (query: string) => void;
  /** Clears the recent search history. Rendered as the section's final menu item. */
  onClearHistory: () => void;
}

/**
 * Recent searches rendered as a React Aria `MenuSection`. Each entry is a menu
 * item that refills the query when activated. The "Clear search history"
 * control is the section's final `MenuItem` so it joins the menu collection —
 * reachable via the dropdown's arrow-key navigation and announced to screen
 * readers, instead of sitting in a `<div>` outside the `Menu` where assistive
 * tech could not find it (ENT-538). The visible "Recent searches" header is
 * rendered outside the `Menu` by the dropdown, so the section carries an
 * `aria-label`.
 */
export function SearchHistory({
  history,
  onSelect,
  onClearHistory,
}: SearchHistoryProps) {
  if (history.length === 0) return;

  return (
    <MenuSection
      id="history"
      aria-label="Recent searches"
      className={styles.section}
    >
      {history.map((entry) => (
        <MenuItem
          key={entry}
          id={`history-${entry}`}
          textValue={entry}
          className={styles.historyItem}
          onAction={() => onSelect(entry)}
        >
          <div className={styles.historyIcon}>
            <Icon name="miscClock" size={16} color="TextWeakest" isBlock />
          </div>
          <Text type="Body1Multiline" color="TextDefault">
            {entry}
          </Text>
        </MenuItem>
      ))}
      <MenuItem
        // Namespaced so it can never collide with a history entry's
        // id={`history-${entry}`} (e.g. a search for "clear").
        id="__clear-search-history-action__"
        textValue="Clear search history"
        className={styles.historyClearItem}
        onAction={onClearHistory}
      >
        <Text type="Body2" color="TextWeakest">
          Clear search history
        </Text>
      </MenuItem>
    </MenuSection>
  );
}
